import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axiosInstance";
import { roleApi, Role } from "../../role/role-api";

interface Prop {
  onClose: () => void;
  id?: any;
}

const UserRegisterFrom: React.FC<Prop> = ({ onClose, id }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [formoData, setFormData] = useState<any>({ role_id: null });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState<null | boolean>(null);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleChage = (key: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = () => {
    console.log("Form data:", formoData);
    if (!formoData?.name) {
      toast.error("Name is required ");
      return;
    }
    if (!validateEmail(formoData?.user_name)) {
      toast.error("Invalid email address");
      return;
    }
    if (!formoData?.password && !id) {
      toast.error("Password is required");
      return;
    }
    // For Super Admin, allow role = 0, otherwise require role_id
    const isSuperAdmin = formoData?.role === 0;
    const hasRoleId = formoData?.role_id && formoData?.role_id > 0;
    
    if (!isSuperAdmin && !hasRoleId) {
      toast.error("Role is required. Please select a role from Role Management.");
      return;
    }
    if (!formoData?.id) {
      handleCreate();
    } else {
      handleUpdate();
    }
  };

  const handleCreate = async () => {
    try {
      const requestData: any = {
        name: formoData.name,
        user_name: formoData.user_name,
        password: formoData.password,
      };
      
      // Super Admin exception: allow role = 0
      if (formoData.role === 0) {
        requestData.role = 0;
      } else if (formoData.role_id) {
        requestData.role_id = Number(formoData.role_id);
        requestData.role = 1; // Default legacy role for non-Super Admin
      } else {
        toast.error("Role is required. Please select a role from Role Management.");
        return;
      }
      
      const resp = await axiosInstance.post(`/api/auth`, requestData);
      if (resp.status === 200) {
        toast.success("User registered successfully");
        onClose();
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.body ||
        error?.response?.data?.message ||
        "Failed to register user. Please try again later."
      );
      return;
    }
  };

  const handleUpdate = async () => {
    try {
      const requestData: any = {
        name: formoData.name,
        user_name: formoData.user_name,
        block: formoData.block || false,
      };
      
      // Include password only if provided
      if (formoData.password) {
        requestData.password = formoData.password;
      }
      
      // Always include role information if it exists in formData
      // Super Admin exception: allow role = 0
      if (formoData.role === 0) {
        requestData.role = 0;
        requestData.role_id = null; // Explicitly set to null for Super Admin
      } else if (formoData.role_id !== undefined && formoData.role_id !== null) {
        // Include role_id if it's been set (even if it's 0, though that shouldn't happen)
        requestData.role_id = Number(formoData.role_id);
      }
      // If role_id is not set, don't include it - backend will keep existing role
      
      console.log("Update request data:", requestData);
      
      const resp = await axiosInstance.put(`/api/auth/${id}`, requestData);
      if (resp.status === 200) {
        toast.success("User updated successfully");
        onClose();
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.body ||
        error?.response?.data?.message ||
        "Failed to update user. Please try again later."
      );
      return;
    }
  };

  const handleGetUser = async () => {
    setLoading(true);
    try {
      const resp: any = await axiosInstance.get(`/api/auth/${id}`);
      if (resp.status === 200) {
        const userData = resp?.data?.data;
        setFormData({
          ...userData,
          role_id: userData?.roleEntity?.id || null,
          role: userData?.role || null,
        });
      }
    } catch (Error: any) {
      toast.error(Error?.response?.data?.body);
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles from Role Management
  const fetchRoles = useCallback(async () => {
    setLoadingRoles(true);
    try {
      // Request roles with excludeSuperAdmin=true to hide Super Admin role from backend
      const rolesData = await roleApi.getAllRoles(undefined, true);
      // Filter only active roles
      const activeRoles = rolesData.filter(role => role.is_active);
      setRoles(activeRoles);
    } catch (error: any) {
      toast.error(error.message || "Failed to load roles");
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    if (id) handleGetUser();
  }, [id, fetchRoles]);

  return (
    <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {id ? "Edit User" : "Create New User"}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            {id
              ? "Update user information"
              : "Fill in the details to create a new user"}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          aria-label="Close"
        >
          <i className="fa-solid fa-xmark text-xl"></i>
        </button>
      </div>

      {/* Loading State */}
      {id && loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-4 font-medium">
            Loading user details...
          </p>
        </div>
      ) : (
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formoData?.name || ""}
              onChange={(e: any) => handleChage("name", e?.target.value)}
              placeholder="Enter full name"
              className="dark-input w-full px-4 py-2.5"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formoData?.user_name || ""}
              onChange={(e: any) => handleChage("user_name", e?.target.value)}
              placeholder="Enter email address"
              className="dark-input w-full px-4 py-2.5"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formoData?.password || ""}
                onChange={(e: any) => handleChage("password", e?.target.value)}
                placeholder="Enter password"
                className="dark-input w-full px-4 py-2.5 pr-10"
              />
              <button
                type="button"
                onClick={handleTogglePassword}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 p-1"
              >
                {showPassword ? (
                  <i className="fa-solid fa-eye-slash"></i>
                ) : (
                  <i className="fa-solid fa-eye"></i>
                )}
              </button>
            </div>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            {loadingRoles ? (
              <div className="dark-input w-full px-4 py-2.5 text-gray-500">
                Loading roles...
              </div>
            ) : (
              <select
                value={formoData?.role === 0 ? 0 : (formoData?.role_id || "")}
                onChange={(e) => {
                  const value = e?.target?.value;
                  if (value === "0") {
                    setFormData((prev: any) => ({ ...prev, role: 0, role_id: null }));
                  } else if (value) {
                    setFormData((prev: any) => ({ ...prev, role_id: Number(value), role: null }));
                  } else {
                    setFormData((prev: any) => ({ ...prev, role_id: null, role: null }));
                  }
                }}
                className="dark-input w-full px-4 py-2.5"
                required
              >
                <option value="">Select a role</option>
                {roles.map((role: Role) => (
                  <option key={role.id} value={role.id}>
                    {role.name} {role.description ? `- ${role.description}` : ""}
                  </option>
                ))}
              </select>
            )}
            {formoData?.roleEntity && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Current role: <span className="font-semibold">{formoData.roleEntity.name}</span>
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="pt-4 flex gap-3">
            {id ? (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-primary flex-1 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmBlock(!formoData?.block)}
                  className="btn-primary flex-1 py-2.5"
                >
                  {formoData?.block ? "Unblock" : "Block"}
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 py-2.5"
                >
                  Update
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-primary flex-1 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1 py-2.5"
                >
                  Create User
                </button>
              </>
            )}
          </div>
        </form>
      )}

      {/* Block/Unblock Confirmation Modal */}
      {confirmBlock !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
          onClick={() => setConfirmBlock(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirm Action
              </h2>
              <button
                onClick={() => setConfirmBlock(null)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {confirmBlock
                ? "Are you sure you want to block this user?"
                : "Are you sure you want to unblock this user?"}
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="btn-primary"
                onClick={() => setConfirmBlock(null)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={async () => {
                  handleChage("block", confirmBlock);
                  try {
                    const resp = await axiosInstance.put(`/api/auth/${id}`, {
                      ...formoData,
                      block: confirmBlock,
                    });
                    if (resp.status === 200) {
                      toast.success(
                        `${formoData?.name || "User"} has been ${
                          confirmBlock ? "blocked" : "unblocked"
                        }`
                      );
                      onClose();
                    }
                  } catch (error: any) {
                    toast.error("Failed to update user status");
                  }
                  setConfirmBlock(null);
                }}
              >
                {confirmBlock ? "Block" : "Unblock"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRegisterFrom;
